import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import {
  FaUser, FaEnvelope, FaPhone, FaLock, FaEye, FaEyeSlash,
  FaTimes, FaUserEdit, FaUserPlus, FaIdCard, FaCheckCircle,
  FaShieldAlt, FaChevronDown, FaChevronUp,
} from 'react-icons/fa';
import { userAPI, roleAPI } from '../../../utils/api';

const AVATAR_COLORS = [
  ['#10ac84', '#0e9a75'],
  ['#0f6fad', '#0d5a8a'],
  ['#8e44ad', '#6c3483'],
  ['#e67e22', '#d35400'],
  ['#e74c3c', '#c0392b'],
  ['#2980b9', '#1a6fa5'],
];

function getInitials(first, last, username) {
  if (first || last) return `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase();
  return (username || 'U').slice(0, 2).toUpperCase();
}

function getAvatarGrad(name) {
  const code = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

function SectionHeader({ label }) {
  return (
    <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.4px', color: '#94a3b8', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span>{label}</span>
      <span style={{ flex: 1, height: 1, background: '#e8eef4' }} />
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', letterSpacing: '0.02em' }}>
        {label} {required && <span style={{ color: '#e74c3c' }}>*</span>}
      </label>
      {children}
      {error && (
        <span style={{ fontSize: '0.73rem', color: '#e74c3c', display: 'flex', alignItems: 'center', gap: 4 }}>
          ● {error}
        </span>
      )}
    </div>
  );
}

const inputStyle = (hasIcon, hasError, disabled) => ({
  width: '100%',
  padding: hasIcon ? '11px 14px 11px 38px' : '11px 14px',
  border: `1.5px solid ${hasError ? '#e74c3c' : '#e2e8f0'}`,
  borderRadius: 10,
  fontSize: '0.9rem',
  fontFamily: "'Poppins', sans-serif",
  background: disabled ? '#f8fafc' : '#fff',
  color: '#1e293b',
  outline: 'none',
  transition: 'border-color 0.18s, box-shadow 0.18s',
  boxSizing: 'border-box',
  opacity: disabled ? 0.75 : 1,
  cursor: disabled ? 'not-allowed' : 'text',
});

function RolesDropdown({ allRoles, selected, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = name => {
    if (disabled) return;
    onChange(selected.includes(name) ? selected.filter(n => n !== name) : [...selected, name]);
  };

  const displayText = selected.length
    ? selected.join(', ')
    : 'Select roles...';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        style={{
          width: '100%', padding: '11px 38px 11px 38px',
          border: `1.5px solid #e2e8f0`, borderRadius: 10,
          background: disabled ? '#f8fafc' : '#fff', color: selected.length ? '#1e293b' : '#94a3b8',
          fontSize: '0.9rem', fontFamily: "'Poppins', sans-serif",
          textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          transition: 'border-color 0.18s, box-shadow 0.18s',
          opacity: disabled ? 0.75 : 1,
          boxSizing: 'border-box',
        }}
      >
        <span style={{ position: 'absolute', left: 12, color: '#94a3b8', fontSize: '0.85rem' }}>
          <FaShieldAlt />
        </span>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.88rem' }}>
          {displayText}
        </span>
        <span style={{ color: '#94a3b8', fontSize: '0.75rem', flexShrink: 0 }}>
          {open ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      </button>

      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
          {selected.map(name => (
            <span key={name} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'rgba(15,111,173,0.1)', color: '#0f6fad',
              borderRadius: 6, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600,
            }}>
              {name}
              {!disabled && (
                <button type="button" onClick={() => toggle(name)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0f6fad', padding: 0, display: 'flex', alignItems: 'center', fontSize: '0.65rem' }}>
                  <FaTimes />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50,
          background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto',
          animation: 'ddFadeIn 0.15s ease',
        }}>
          <style>{`@keyframes ddFadeIn { from { opacity:0; transform:translateY(-6px) } to { opacity:1; transform:none } }`}</style>
          {allRoles.length === 0 && (
            <div style={{ padding: '12px 14px', color: '#94a3b8', fontSize: '0.85rem' }}>No roles available</div>
          )}
          {allRoles.map(role => {
            const isSelected = selected.includes(role.name);
            return (
              <div
                key={role.id || role.name}
                onClick={() => toggle(role.name)}
                style={{
                  padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                  background: isSelected ? 'rgba(15,111,173,0.06)' : 'transparent',
                  transition: 'background 0.12s',
                  fontSize: '0.875rem', color: isSelected ? '#0f6fad' : '#374151',
                  fontWeight: isSelected ? 600 : 400,
                }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { e.currentTarget.style.background = isSelected ? 'rgba(15,111,173,0.06)' : 'transparent'; }}
              >
                <span style={{
                  width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                  border: `2px solid ${isSelected ? '#0f6fad' : '#d1d5db'}`,
                  background: isSelected ? '#0f6fad' : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s',
                }}>
                  {isSelected && <FaCheckCircle style={{ color: '#fff', fontSize: '0.6rem' }} />}
                </span>
                {role.name}
                {role.description && (
                  <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginLeft: 'auto' }}>{role.description}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function UserModal({ mode = 'create', user = null, onClose, onSaved }) {
  const isView = mode === 'view';
  const isEdit = mode === 'edit';

  const [form, setForm] = useState({
    username: '', email: '', firstName: '', lastName: '',
    phone: '', user_type: '', status: 'ACTIVE', password: '', confirm: '',
  });
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Load all available roles once
  useEffect(() => {
    roleAPI.getRoles()
      .then(res => setAllRoles(res.data?.content || res.data || []))
      .catch(() => {});
  }, []);

  // Populate form from user object (DB returns snake_case)
  useEffect(() => {
    if (user) {
      setForm({
        username: user.username || '',
        email: user.email || '',
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        phone: user.phone || '',
        user_type: user.user_type || '',
        status: user.status || 'ACTIVE',
        password: '',
        confirm: '',
      });
      // Pre-populate roles from Roles array (capital R from Sequelize)
      const existingRoles = (user.Roles || []).map(r => r.name.replace(/^ROLE_/, ''));
      setSelectedRoles(existingRoles);
    }
  }, [user]);

  const validate = () => {
    const e = {};
    if (!form.username.trim()) e.username = 'Username is required';
    else if (form.username.length < 3) e.username = 'Min 3 characters';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email format';
    if (mode === 'create' && !form.password) e.password = 'Password is required';
    if (mode === 'create' && form.password && form.password.length < 6) e.password = 'Min 6 characters';
    if (form.password && form.password !== form.confirm) e.confirm = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const change = e => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors(prev => ({ ...prev, [e.target.name]: '' }));
  };

  const submit = async e => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        username: form.username,
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        user_type: form.user_type,
        status: form.status,
        roles: selectedRoles,
      };
      if (form.password) payload.password = form.password;

      if (isEdit) await userAPI.updateUser(user.id, payload);
      else await userAPI.createUser(payload);

      await Swal.fire({
        title: isEdit ? 'User Updated!' : 'User Created!',
        text: `${form.username} has been ${isEdit ? 'updated' : 'created'} successfully.`,
        icon: 'success',
        confirmButtonColor: '#10ac84',
        confirmButtonText: 'Great!',
        timer: 2500,
        timerProgressBar: true,
      });
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      let msg = err?.response?.data?.message || err?.response?.data?.error || err.message || 'An error occurred';
      if (msg === 'USERNAME_EXISTS') msg = 'This username is already taken';
      if (msg === 'EMAIL_EXISTS') msg = 'This email is already registered';
      Swal.fire({ title: 'Error', text: msg, icon: 'error', confirmButtonColor: '#e74c3c' });
    } finally {
      setSubmitting(false);
    }
  };

  const initials = getInitials(form.firstName, form.lastName, form.username);
  const [gradStart, gradEnd] = getAvatarGrad(form.username);

  const titleMap = { create: 'Create New User', edit: 'Edit User', view: 'User Details' };
  const IconMap  = { create: FaUserPlus, edit: FaUserEdit, view: FaIdCard };
  const TitleIcon = IconMap[mode] || FaUserPlus;

  return (
    <div style={{ fontFamily: "'Poppins', sans-serif", minWidth: 0 }}>
      <style>{`
        .um-input:focus { border-color: #10ac84 !important; box-shadow: 0 0 0 3px rgba(16,172,132,0.12) !important; background: #fff !important; }
        .um-input-error:focus { border-color: #e74c3c !important; box-shadow: 0 0 0 3px rgba(231,76,60,0.1) !important; }
        .um-btn-save:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(16,172,132,0.4) !important; }
        .um-btn-cancel:hover { background: #e2e8f0 !important; color: #1e293b !important; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 24px 16px',
        background: 'linear-gradient(135deg, #f0fff8 0%, #f0f7ff 100%)',
        borderBottom: '1px solid #e8f4f0',
        borderRadius: '18px 18px 0 0',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12, flexShrink: 0,
            background: 'linear-gradient(135deg, #10ac84 0%, #0f6fad 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '1rem', boxShadow: '0 4px 12px rgba(16,172,132,0.3)',
          }}>
            <TitleIcon />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a', lineHeight: 1.3 }}>
              {titleMap[mode]}
            </div>
            {isEdit && user?.username && (
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
                Editing: {user.username}
                {user.id && <span style={{ marginLeft: 8, background: 'rgba(15,111,173,0.1)', color: '#0f6fad', borderRadius: 4, padding: '1px 6px', fontSize: '0.7rem', fontWeight: 700 }}>#{user.id}</span>}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer',
            background: 'rgba(0,0,0,0.06)', color: '#64748b', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem',
            transition: 'all 0.15s', flexShrink: 0,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(231,76,60,0.1)'; e.currentTarget.style.color = '#e74c3c'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; e.currentTarget.style.color = '#64748b'; }}
        >
          <FaTimes />
        </button>
      </div>

      <form onSubmit={submit}>
        <div style={{ padding: '24px 24px 8px' }}>

          {/* Avatar preview */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, padding: 16, background: '#f8fafc', borderRadius: 14, border: '1px solid #e8eef4' }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14, flexShrink: 0,
              background: `linear-gradient(135deg, ${gradStart} 0%, ${gradEnd} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: 700, fontSize: '1.2rem',
              boxShadow: `0 4px 14px ${gradStart}55`,
              letterSpacing: 1,
            }}>
              {initials || <FaUser style={{ opacity: 0.8 }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.95rem' }}>
                {[form.firstName, form.lastName].filter(Boolean).join(' ') || form.username || 'New User'}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
                {form.email || 'No email set'} {form.user_type ? `· ${form.user_type}` : ''}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 8px', borderRadius: 20,
                  background: form.status === 'ACTIVE' ? 'rgba(16,172,132,0.1)' : 'rgba(231,76,60,0.1)',
                  color: form.status === 'ACTIVE' ? '#10ac84' : '#e74c3c',
                  fontSize: '0.7rem', fontWeight: 700,
                }}>
                  <FaCheckCircle style={{ fontSize: '0.6rem' }} />
                  {form.status}
                </span>
                {selectedRoles.map(r => (
                  <span key={r} style={{
                    padding: '2px 8px', borderRadius: 20,
                    background: 'rgba(15,111,173,0.1)', color: '#0f6fad',
                    fontSize: '0.7rem', fontWeight: 700,
                  }}>{r}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Section: Account Info */}
          <div style={{ marginBottom: 18 }}>
            <SectionHeader label="Account Information" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
              <Field label="Username" required error={errors.username}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <span style={{ position: 'absolute', left: 12, color: '#94a3b8', fontSize: '0.85rem', pointerEvents: 'none', zIndex: 1 }}><FaUser /></span>
                  <input
                    className={`um-input${errors.username ? ' um-input-error' : ''}`}
                    name="username" value={form.username} onChange={change}
                    disabled={isView || submitting} placeholder="e.g. john.doe"
                    style={inputStyle(true, !!errors.username, isView || submitting)}
                  />
                </div>
              </Field>
              <Field label="Email Address" required error={errors.email}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <span style={{ position: 'absolute', left: 12, color: '#94a3b8', fontSize: '0.85rem', pointerEvents: 'none', zIndex: 1 }}><FaEnvelope /></span>
                  <input
                    className={`um-input${errors.email ? ' um-input-error' : ''}`}
                    name="email" type="email" value={form.email} onChange={change}
                    disabled={isView || submitting} placeholder="user@example.com"
                    style={inputStyle(true, !!errors.email, isView || submitting)}
                  />
                </div>
              </Field>
            </div>
          </div>

          {/* Section: Personal Details */}
          <div style={{ marginBottom: 18 }}>
            <SectionHeader label="Personal Details" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
              <Field label="First Name" error={errors.firstName}>
                <input
                  className="um-input" name="firstName" value={form.firstName} onChange={change}
                  disabled={isView || submitting} placeholder="First name"
                  style={inputStyle(false, false, isView || submitting)}
                />
              </Field>
              <Field label="Last Name" error={errors.lastName}>
                <input
                  className="um-input" name="lastName" value={form.lastName} onChange={change}
                  disabled={isView || submitting} placeholder="Last name"
                  style={inputStyle(false, false, isView || submitting)}
                />
              </Field>
              <Field label="Phone Number" error={errors.phone}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <span style={{ position: 'absolute', left: 12, color: '#94a3b8', fontSize: '0.85rem', pointerEvents: 'none', zIndex: 1 }}><FaPhone /></span>
                  <input
                    className="um-input" name="phone" value={form.phone} onChange={change}
                    disabled={isView || submitting} placeholder="+92 300 0000000"
                    style={inputStyle(true, false, isView || submitting)}
                  />
                </div>
              </Field>
              <Field label="User Type">
                <input
                  className="um-input" name="user_type" value={form.user_type || ''} onChange={change}
                  disabled={isView || submitting} placeholder="e.g. ADMIN, OFFICER"
                  style={inputStyle(false, false, isView || submitting)}
                />
              </Field>
              <Field label="Status" required>
                <select
                  className="um-input" name="status" value={form.status} onChange={change}
                  disabled={isView || submitting}
                  style={{ ...inputStyle(false, false, isView || submitting), cursor: isView || submitting ? 'not-allowed' : 'pointer' }}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </Field>
            </div>
          </div>

          {/* Section: Roles */}
          <div style={{ marginBottom: 18 }}>
            <SectionHeader label="Roles & Access" />
            <Field label="Assign Roles">
              <RolesDropdown
                allRoles={allRoles}
                selected={selectedRoles}
                onChange={setSelectedRoles}
                disabled={isView}
              />
            </Field>
          </div>

          {/* Section: Security */}
          {!isView && (
            <div style={{ marginBottom: 8 }}>
              <SectionHeader label={isEdit ? 'Change Password (optional)' : 'Set Password'} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
                <Field label={isEdit ? 'New Password' : 'Password'} required={mode === 'create'} error={errors.password}>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.85rem', zIndex: 1, pointerEvents: 'none' }}><FaLock /></span>
                    <input
                      className={`um-input${errors.password ? ' um-input-error' : ''}`}
                      name="password" type={showPwd ? 'text' : 'password'}
                      value={form.password} onChange={change}
                      disabled={submitting} placeholder="Min 6 characters"
                      style={{ ...inputStyle(true, !!errors.password, submitting), paddingRight: 38 }}
                    />
                    <button type="button" onClick={() => setShowPwd(v => !v)}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, display: 'flex', alignItems: 'center', fontSize: '0.85rem' }}>
                      {showPwd ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </Field>
                <Field label="Confirm Password" required={mode === 'create'} error={errors.confirm}>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.85rem', zIndex: 1, pointerEvents: 'none' }}><FaLock /></span>
                    <input
                      className={`um-input${errors.confirm ? ' um-input-error' : ''}`}
                      name="confirm" type={showConfirm ? 'text' : 'password'}
                      value={form.confirm} onChange={change}
                      disabled={submitting} placeholder="Re-enter password"
                      style={{ ...inputStyle(true, !!errors.confirm, submitting), paddingRight: 38 }}
                    />
                    <button type="button" onClick={() => setShowConfirm(v => !v)}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, display: 'flex', alignItems: 'center', fontSize: '0.85rem' }}>
                      {showConfirm ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </Field>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', gap: 10, justifyContent: 'flex-end',
          padding: '14px 24px 20px',
          borderTop: '1px solid #f0f4f8',
          background: '#fafbfc',
          borderRadius: '0 0 18px 18px',
          position: 'sticky', bottom: 0, zIndex: 10,
        }}>
          <button type="button" className="um-btn-cancel" onClick={onClose}
            style={{
              padding: '10px 20px', borderRadius: 10, border: '1.5px solid #e2e8f0',
              background: '#f1f5f9', color: '#475569', fontFamily: "'Poppins', sans-serif",
              fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.15s',
            }}>
            {isView ? 'Close' : 'Cancel'}
          </button>
          {!isView && (
            <button type="submit" className="um-btn-save" disabled={submitting}
              style={{
                padding: '10px 24px', borderRadius: 10, border: 'none',
                background: 'linear-gradient(135deg, #10ac84 0%, #0e9a75 100%)',
                color: '#fff', fontFamily: "'Poppins', sans-serif",
                fontWeight: 600, fontSize: '0.875rem', cursor: submitting ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 12px rgba(16,172,132,0.3)', transition: 'all 0.18s',
                opacity: submitting ? 0.65 : 1, display: 'flex', alignItems: 'center', gap: 8,
              }}>
              {submitting ? (
                <>
                  <span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                  Saving...
                </>
              ) : (
                isEdit ? 'Save Changes' : 'Create User'
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

UserModal.propTypes = {
  mode: PropTypes.string,
  user: PropTypes.object,
  onClose: PropTypes.func,
  onSaved: PropTypes.func,
};
